






















































































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import router from "@/router/router";
import DatePickerComponent from "@/components/common/DatePicker.vue";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { ComponentName } from "@/utils/RouteGuards";
import {
  DeviceReportFilters,
  emptyDeviceReportFilters,
  fromQueryParams,
  toQueryParams,
} from "@/data/models/DeviceReportFilters";
import { DeviceReportDeviceCategory, DeviceReportType } from "@/data/models/DeviceReport";
import { formatDateToYearMonthDateString } from "@/utils/DateHelper";
import { loadDeviceReportRows } from "@/data/repository/DeviceReportLoader";
import { themed } from "@/utils/Theme";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";

/**
 * The Testberichte filter screen.
 *
 * Same shape as the RENO review queue, deliberately: a count in the sticky header, one filter card,
 * and a fixed action button. An operator moving between the two admin areas should not have to
 * learn a second layout.
 *
 * The counting is a real load rather than an aggregate query. Firestore's `count()` would need the
 * same indexes as the query itself, and the page is bounded anyway, so counting by loading keeps
 * one code path and one set of rules about what matches.
 */
const COUNT_DEBOUNCE_MS = 300;

@Component({
  components: { "date-picker": DatePickerComponent },
})
export default class DeviceReportListComponent extends Vue {
  filters: DeviceReportFilters = emptyDeviceReportFilters();
  reportCount = 0;
  isCounting = false;
  isPartialCount = false;
  countError = false;

  private countTimer: number | null = null;

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get today(): string {
    return formatDateToYearMonthDateString(new Date());
  }

  get deviceCategoryItems() {
    return [
      { value: DeviceReportDeviceCategory.COM_BOX, text: this.$t("admin.deviceReports.categoryComBox") },
      { value: DeviceReportDeviceCategory.SENSOR, text: this.$t("admin.deviceReports.categorySensor") },
      { value: DeviceReportDeviceCategory.PANEL, text: this.$t("admin.deviceReports.categoryPanel") },
    ];
  }

  /** Driven by the enum, so a new report type appears here the moment the model gains one. */
  get reportTypeItems() {
    return Object.values(DeviceReportType).map((type) => ({ value: type, text: type }));
  }

  get monthItems() {
    return [1, 3, 6, 12, 24, 36].map((months) => ({
      value: months,
      text: `${months} ${this.$t("admin.deviceReports.monthsSuffix")}`,
    }));
  }

  /** The one filter combination that silently drops a whole source, so the screen says so. */
  get isCategoryHidingUnassigned(): boolean {
    return this.filters.deviceCategories.length > 0 && this.filters.assignment !== "assigned";
  }

  get reportCountValue(): string {
    if (this.countError) {
      return this.$t("admin.deviceReports.loadFailed").toString();
    }

    // A grey "–" while counting, as every portal header shows a figure not known yet.
    if (this.isCounting) {
      return "–";
    }

    if (this.reportCount === 0) {
      return this.$t("admin.deviceReports.countNone").toString();
    }

    // "ca." past a full page: the number is a lower bound, and claiming a total we did not count
    // would be a lie the operator cannot check.
    return this.isPartialCount
      ? this.$t("admin.deviceReports.countApprox", [this.reportCount]).toString()
      : String(this.reportCount);
  }

  /**
   * Recount on any filter change, debounced.
   *
   * Deep, because the filters object is mutated in place by the controls above; without it typing a
   * serial would not recount.
   */
  @Watch("filters", { deep: true })
  onFiltersChanged() {
    if (this.countTimer !== null) {
      window.clearTimeout(this.countTimer);
    }

    this.countTimer = window.setTimeout(() => this.count(), COUNT_DEBOUNCE_MS);
  }

  onShowReports() {
    router.push({
      name: ComponentName.DEVICE_REPORT_RESULTS,
      query: toQueryParams(this.normalisedFilters()),
    });
  }

  private normalisedFilters(): DeviceReportFilters {
    return {
      ...this.filters,
      // Trimmed and upper-cased here rather than in the field, so the operator's cursor is never
      // moved while they type.
      serialNo: (this.filters.serialNo ?? "").trim().toUpperCase(),
    };
  }

  private async count() {
    this.isCounting = true;
    this.countError = false;

    try {
      const result = await loadDeviceReportRows(this.normalisedFilters());

      this.reportCount = result.rows.length;
      this.isPartialCount = result.isPartial;
    } catch {
      // Swallowed on purpose: the header shows its own failure state and there is nothing to act on
      // beyond changing the filters or retrying.
      this.countError = true;
      this.reportCount = 0;
    } finally {
      this.isCounting = false;
    }
  }

  mounted() {
    ToolbarContentModule.setI18nResource("admin.deviceReports.title");

    // Filters survive a round trip to the results screen and back, so the browser's back button
    // returns to the question the operator asked rather than to an empty form.
    this.filters = fromQueryParams(this.$route.query as Record<string, unknown>);
    this.count();
  }

  beforeDestroy() {
    if (this.countTimer !== null) {
      window.clearTimeout(this.countTimer);
    }
  }
}
